import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Calculator, TrendingUp, TrendingDown, Save, FileDown, Printer,
  Building2, User, ArrowRight, DollarSign, BadgePercent, Wallet,
  Sparkles, RefreshCw, FileText, Users,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Cell, LineChart, Line, Legend,
} from "recharts";
import { toast } from "sonner";
import { PageHero, KpiCard, Section, GridSkeleton } from "@/components/AnalyticsCommon";
import CltPjReportDialog from "@/components/CltPjReportDialog";
import MultiScenarioDialog from "@/components/MultiScenarioDialog";
import CltPjReportsHistoryPanel from "@/components/CltPjReportsHistoryPanel";
import CltPjScheduledReportsPanel from "@/components/CltPjScheduledReportsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";

const fmtBRL = (v) => `R$ ${(v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${(v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const SEMAFORO_COLOR = {
  verde: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", chart: "#059669" },
  amarelo: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", chart: "#D97706" },
  vermelho: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", chart: "#E11D48" },
};

function semColor(s) {
  return SEMAFORO_COLOR[s] || SEMAFORO_COLOR.amarelo;
}

const DEFAULT_CLT = {
  salario_base: 5000,
  dependentes: 0,
  beneficios: 0,
  inss_patronal_pct: 0.20,
  rat_fap_pct: 0.03,
  terceiros_pct: 0.058,
  fgts_pct: 0.08,
  multa_fgts_pct: 0.40,
  aposentadoria_especial: false,
};

const DEFAULT_PJ = {
  valor_mensal: 7000,
  regime: "simples_iii",
  fator_r: true,
  iss_pct: 0.05,
  rbt12: null,
  custo_contador: null,
};

export default function SimuladorCltPjPage() {
  const today = new Date();
  const [searchParams] = useSearchParams();
  const [clt, setClt] = useState(() => {
    const sal = parseFloat(searchParams.get("salario"));
    return { ...DEFAULT_CLT, salario_base: sal && sal > 0 ? sal : DEFAULT_CLT.salario_base };
  });
  const [pj, setPj] = useState(() => {
    const sal = parseFloat(searchParams.get("salario"));
    // Sugestão PJ: salário CLT × 1.4 (compensa encargos do contratado)
    return { ...DEFAULT_PJ, valor_mensal: sal && sal > 0 ? Math.round(sal * 1.4) : DEFAULT_PJ.valor_mensal };
  });
  const prefilledName = searchParams.get("nome");
  const prefilledCargo = searchParams.get("cargo");
  const [result, setResult] = useState(null);
  const [tabelas, setTabelas] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [nomeCenario, setNomeCenario] = useState("");
  const [competenciaMes, setCompetenciaMes] = useState(today.getMonth() + 1);
  const [competenciaAno, setCompetenciaAno] = useState(today.getFullYear());

  // History & dashboard
  const [history, setHistory] = useState(null);
  const [dashFilters, setDashFilters] = useState({ month: null, year: today.getFullYear() });
  const [tab, setTab] = useState("simulador");
  const [reportOpen, setReportOpen] = useState(false);
  const [multiOpen, setMultiOpen] = useState(false);
  const { user } = useAuth();
  const { selectedCompany } = useCompany();

  // Load tabelas vigentes (dependendo da competência selecionada)
  useEffect(() => {
    api.get("/clt-pj/tabelas", { params: { year: competenciaAno } })
      .then((r) => setTabelas(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [competenciaAno]);

  const runSimulation = async () => {
    // Guard: campos obrigatórios válidos antes de chamar API (evita 422 spam)
    const salarioOk = Number(clt?.salario_base) > 0;
    const pjOk = Number(pj?.valor_mensal) > 0;
    if (!salarioOk || !pjOk) {
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post("/clt-pj/simulate", {
        clt, pj, competencia_ano: competenciaAno,
      });
      setResult(data);
    } catch (e) {
      // Silencia 422 (validação) no auto-run; mostra outros erros
      if (e?.response?.status !== 422) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setCalculating(false); }
  };

  // Auto-run when inputs change (debounced)
  useEffect(() => {
    const t = setTimeout(runSimulation, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(clt), JSON.stringify(pj), competenciaAno]);

  const loadDashboard = async () => {
    try {
      const params = {};
      if (dashFilters.month) params.month = dashFilters.month;
      if (dashFilters.year) params.year = dashFilters.year;
      const { data } = await api.get("/clt-pj/dashboard", { params });
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (tab === "historico") loadDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dashFilters.month, dashFilters.year]);

  const saveSimulation = async () => {
    try {
      await api.post("/clt-pj/simulations", {
        nome_cenario: nomeCenario || `Cenário ${today.toLocaleDateString("pt-BR")}`,
        competencia_mes: competenciaMes,
        competencia_ano: competenciaAno,
        clt, pj,
      });
      toast.success("Simulação salva no histórico");
      setNomeCenario("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteSimulation = async (sid) => {
    if (!window.confirm("Excluir esta simulação?")) return;
    try {
      await api.delete(`/clt-pj/simulations/${sid}`);
      toast.success("Excluída");
      loadDashboard();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const printPdf = () => window.print();

  const exportExcel = () => {
    if (!result) return;
    const csv = [
      ["Simulador CLT × PJ — TheoKratos RH"],
      ["Vigência tabelas", result.vigencia_tabelas],
      [],
      ["=== CLT ==="],
      ["Salário base", clt.salario_base],
      ["Dependentes", clt.dependentes],
      ["Benefícios", clt.beneficios],
      ["INSS empregado", result.clt.descontos.inss.valor],
      ["IRRF", result.clt.descontos.irrf.valor],
      ["FGTS", result.clt.proventos.fgts],
      ["Multa FGTS (prov.)", result.clt.proventos.multa_fgts_provisao],
      ["INSS patronal", result.clt.encargos_empresa.inss_patronal],
      ["RAT/FAP", result.clt.encargos_empresa.rat_fap],
      ["Terceiros", result.clt.encargos_empresa.terceiros],
      ["Custo empresa mensal", result.clt.custo_empresa_mensal],
      ["Custo empresa anual", result.clt.custo_empresa_anual],
      ["Líquido colaborador mensal", result.clt.liquido_colaborador_mensal],
      [],
      ["=== PJ ==="],
      ["Valor NF mensal", pj.valor_mensal],
      ["Regime", result.pj.anexo],
      ["DAS / Tributos", result.pj.total_tributos],
      ["Contador", result.pj.custo_contador],
      ["Líquido mensal", result.pj.liquido_colaborador_mensal],
      ["Custo empresa anual", result.pj.custo_empresa_anual],
      [],
      ["=== COMPARATIVO ==="],
      ["Economia empresa anual", result.comparativo.custo_empresa.economia_anual],
      ["Economia %", result.comparativo.custo_empresa.economia_pct],
      ["Diferença líquido colaborador anual", result.comparativo.liquido_colaborador.diferenca_anual],
      ["Recomendação", result.comparativo.recomendacao],
    ].map((row) => row.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `simulacao-clt-pj-${today.toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const c = result?.clt;
  const p = result?.pj;
  const cm = result?.comparativo;
  const semEmpresa = cm ? semColor(cm.custo_empresa.semaforo) : semColor("amarelo");

  const compareData = result ? [
    { name: "Custo Empresa", CLT: cm.custo_empresa.clt_anual, PJ: cm.custo_empresa.pj_anual },
    { name: "Líquido Colaborador", CLT: cm.liquido_colaborador.clt_anual, PJ: cm.liquido_colaborador.pj_anual },
    { name: "Encargos/Tributos", CLT: cm.encargos_totais.clt * 12, PJ: cm.encargos_totais.pj_tributos_e_contador * 12 },
  ] : [];

  return (
    <div className="space-y-4 pb-10" data-testid="simulador-clt-pj-page">
      <PageHero
        kicker={<><Calculator className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Inteligência Financeira</>}
        title="Simulador CLT × PJ"
        description={`Tabelas vigentes ${tabelas?.vigencia || "2026"} (${tabelas?.legal_basis || "Lei 15.191/2025 + Lei 15.270/2025"}) · Salário mínimo: ${fmtBRL(tabelas?.salario_minimo_nacional || 1518)} · Teto INSS: ${fmtBRL(tabelas?.teto_inss || 8475.55)} · Dependente IRRF: ${fmtBRL(tabelas?.irrf_dependente || 189.59)}.`}
        actions={
          <>
            <Button size="sm" variant="outline" onClick={runSimulation}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="sim-recalc">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Recalcular
            </Button>
            <Button size="sm" onClick={exportExcel}
                    className="h-10 bg-white/15 hover:bg-white/25 text-white"
                    data-testid="sim-export-excel">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Excel
            </Button>
            <Button size="sm" onClick={printPdf}
                    className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                    data-testid="sim-print">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> PDF/Impressão
            </Button>
            <Button size="sm" onClick={() => setReportOpen(true)} disabled={!result}
                    className="h-10 bg-white text-[#234935] hover:bg-slate-100 font-semibold"
                    data-testid="sim-open-report">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Gerar Relatório para Diretoria
            </Button>
            <Button size="sm" onClick={() => setMultiOpen(true)}
                    className="h-10 bg-white text-[#0F3B6B] hover:bg-slate-100 font-semibold"
                    data-testid="sim-open-multi">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Comparação Múltipla
            </Button>
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="simulador" data-testid="tab-simulador">Simulador</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico & Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="simulador" className="space-y-4 mt-4">
          {prefilledName && (
            <div className="rounded-lg bg-gradient-to-r from-[#C5A059]/10 to-emerald-50 border border-[#C5A059]/30 px-4 py-2.5 flex items-center gap-3 text-[12.5px]"
                 data-testid="prefilled-banner">
              <Sparkles className="h-4 w-4 text-[#C5A059]" />
              <span>Simulação pré-preenchida para <b>{prefilledName}</b>{prefilledCargo ? ` · ${prefilledCargo}` : ""}. Salário CLT importado e valor PJ sugerido (1,4× CLT).</span>
            </div>
          )}
          {/* Two-column comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ===== COLUNA ESQUERDA — CLT ===== */}
            <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm"
                 data-testid="col-clt">
              <div className="px-4 py-3 bg-gradient-to-r from-emerald-700 to-emerald-800 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="h-4 w-4" />
                  <h3 className="font-bold tracking-wide">CONTRATAÇÃO CLT</h3>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-emerald-100 bg-white/10 px-2 py-0.5 rounded">Carteira Assinada</span>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">Salário Base</Label>
                    <Input type="number" step="0.01" value={clt.salario_base}
                           onChange={(e) => setClt({ ...clt, salario_base: parseFloat(e.target.value) || 0 })}
                           data-testid="clt-salario" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">Benefícios (R$)</Label>
                    <Input type="number" step="0.01" value={clt.beneficios}
                           onChange={(e) => setClt({ ...clt, beneficios: parseFloat(e.target.value) || 0 })}
                           data-testid="clt-beneficios" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">Dependentes IRRF</Label>
                    <Input type="number" min="0" value={clt.dependentes}
                           onChange={(e) => setClt({ ...clt, dependentes: parseInt(e.target.value) || 0 })}
                           data-testid="clt-dependentes" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">INSS Patronal (%)</Label>
                    <Input type="number" step="0.01" value={clt.inss_patronal_pct * 100}
                           onChange={(e) => setClt({ ...clt, inss_patronal_pct: (parseFloat(e.target.value) || 0) / 100 })}
                           data-testid="clt-inss-pat" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">RAT/FAP (%)</Label>
                    <Input type="number" step="0.01" value={clt.rat_fap_pct * 100}
                           onChange={(e) => setClt({ ...clt, rat_fap_pct: (parseFloat(e.target.value) || 0) / 100 })}
                           data-testid="clt-rat" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-emerald-900">Terceiros (%)</Label>
                    <Input type="number" step="0.01" value={clt.terceiros_pct * 100}
                           onChange={(e) => setClt({ ...clt, terceiros_pct: (parseFloat(e.target.value) || 0) / 100 })}
                           data-testid="clt-terc" />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                  <Label className="text-[12px] font-medium text-emerald-900">Aposentadoria Especial</Label>
                  <Switch checked={clt.aposentadoria_especial}
                          onCheckedChange={(v) => setClt({ ...clt, aposentadoria_especial: v })}
                          data-testid="clt-apos" />
                </div>

                {/* Results CLT */}
                {c && (
                  <div className="space-y-2 mt-3 pt-3 border-t-2 border-dashed border-emerald-200">
                    <div className="text-[10.5px] uppercase tracking-wider text-emerald-900 font-bold mb-1">Proventos & Provisões</div>
                    <ResultRow label="Salário mensal" value={c.proventos.salario_base} />
                    <ResultRow label="1/12 férias" value={c.proventos.ferias_1_12} />
                    <ResultRow label="1/3 constitucional férias" value={c.proventos.terco_ferias} />
                    <ResultRow label="1/12 13º salário" value={c.proventos.decimo_terceiro_1_12} />
                    <ResultRow label="FGTS (8%)" value={c.proventos.fgts} />
                    <ResultRow label="Multa FGTS (40%) — provisão" value={c.proventos.multa_fgts_provisao} />

                    <div className="text-[10.5px] uppercase tracking-wider text-emerald-900 font-bold mt-3 mb-1">Descontos Detalhados por Verba</div>
                    <div className="rounded-md bg-white border border-emerald-200 p-2.5 space-y-1.5 text-[11.5px]">
                      <div className="flex justify-between">
                        <span><b>INSS mensal</b> <span className="text-muted-foreground">({c.descontos.inss_mensal.aliquota_efetiva}%)</span></span>
                        <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.inss_mensal.valor)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span><b>IRRF mensal</b> <span className="text-muted-foreground">({c.descontos.irrf_mensal.aliquota}%)</span></span>
                        <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.irrf_mensal.valor)}</span>
                      </div>
                      {c.descontos.irrf_mensal.reducao_aplicada > 0 && (
                        <div className="text-[9.5px] text-emerald-700 ml-2">
                          ↳ Redução mensal aplicada: − {fmtBRL(c.descontos.irrf_mensal.reducao_aplicada)} (Lei 15.270/2025)
                        </div>
                      )}
                      {c.descontos.inss_13o && (
                        <div className="border-t border-dashed border-emerald-200 pt-1.5">
                          <div className="flex justify-between">
                            <span><b>INSS 13º</b> rateado <span className="text-[10px] text-muted-foreground">(integral ÷ 12)</span></span>
                            <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.inss_13o.valor_rateado)}</span>
                          </div>
                          <div className="text-[9.5px] text-muted-foreground ml-2">↳ {c.descontos.inss_13o.memoria}</div>
                        </div>
                      )}
                      {c.descontos.inss_ferias && (
                        <div>
                          <div className="flex justify-between">
                            <span><b>INSS férias</b> rateado <span className="text-[10px] text-muted-foreground">(salário + 1/3, ÷ 12)</span></span>
                            <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.inss_ferias.valor_rateado)}</span>
                          </div>
                          <div className="text-[9.5px] text-muted-foreground ml-2">↳ {c.descontos.inss_ferias.memoria}</div>
                        </div>
                      )}
                      <div className="border-t border-dashed border-emerald-200 pt-1.5">
                        <div className="flex justify-between">
                          <span><b>IRRF 13º</b> rateado <span className="text-[10px] text-muted-foreground">(integral ÷ 12)</span></span>
                          <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.irrf_13o.valor_rateado)}</span>
                        </div>
                        <div className="text-[9.5px] text-muted-foreground ml-2">↳ {c.descontos.irrf_13o.memoria}</div>
                      </div>
                      <div>
                        <div className="flex justify-between">
                          <span><b>IRRF férias</b> rateado <span className="text-[10px] text-muted-foreground">(salário + 1/3, ÷ 12)</span></span>
                          <span className="font-mono text-rose-700">− {fmtBRL(c.descontos.irrf_ferias.valor_rateado)}</span>
                        </div>
                        <div className="text-[9.5px] text-muted-foreground ml-2">↳ {c.descontos.irrf_ferias.memoria}</div>
                      </div>
                      <div className="border-t border-emerald-300 pt-1.5 flex justify-between font-bold">
                        <span>TOTAL descontos</span>
                        <span className="font-mono text-rose-800">− {fmtBRL(c.descontos.total)}</span>
                      </div>
                    </div>

                    <div className="text-[10.5px] uppercase tracking-wider text-emerald-900 font-bold mt-3 mb-1">Encargos Empresa</div>
                    <ResultRow label={`INSS patronal (${(c.encargos_empresa.inss_patronal_rate * 100).toFixed(1)}%)`} value={c.encargos_empresa.inss_patronal} />
                    <ResultRow label="RAT/FAP" value={c.encargos_empresa.rat_fap} />
                    <ResultRow label="Sistema S/Terceiros" value={c.encargos_empresa.terceiros} />
                    <ResultRow label="Encargos totais" value={c.encargos_empresa.total} bold />

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-emerald-100/50 border border-emerald-300 p-3" data-testid="clt-result-liquido">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold mb-1">Líquido em folha</div>
                        <div className="text-xl font-bold text-emerald-800 tabular-nums">{fmtBRL(c.liquido_folha_mensal)}</div>
                        <div className="text-[10px] text-emerald-700 mt-0.5">recebimento direto / mês</div>
                        <div className="text-[10.5px] text-emerald-900 mt-1 pt-1 border-t border-emerald-200">
                          + Provisionado: <b>{fmtBRL(c.liquido_provisionado_mensal)}</b>
                        </div>
                      </div>
                      <div className="rounded-lg bg-emerald-100/50 border border-emerald-300 p-3" data-testid="clt-result-custo">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold mb-1">Custo Empresa</div>
                        <div className="text-xl font-bold text-emerald-800 tabular-nums">{fmtBRL(c.custo_empresa_mensal)}</div>
                        <div className="text-[10.5px] text-emerald-700 mt-0.5">/ mês · {fmtBRL(c.custo_empresa_anual)} / ano</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ===== COLUNA DIREITA — PJ ===== */}
            <div className="rounded-xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/50 to-white shadow-sm"
                 data-testid="col-pj">
              <div className="px-4 py-3 bg-gradient-to-r from-sky-700 to-sky-800 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <User className="h-4 w-4" />
                  <h3 className="font-bold tracking-wide">CONTRATAÇÃO PJ</h3>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-sky-100 bg-white/10 px-2 py-0.5 rounded">Pessoa Jurídica</span>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-sky-900">Valor Mensal NF</Label>
                    <Input type="number" step="0.01" value={pj.valor_mensal}
                           onChange={(e) => setPj({ ...pj, valor_mensal: parseFloat(e.target.value) || 0 })}
                           data-testid="pj-valor" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-sky-900">Regime Tributário</Label>
                    <Select value={pj.regime} onValueChange={(v) => setPj({ ...pj, regime: v })}>
                      <SelectTrigger data-testid="pj-regime"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simples_iii">Simples Nacional · Anexo III</SelectItem>
                        <SelectItem value="simples_v">Simples Nacional · Anexo V</SelectItem>
                        <SelectItem value="mei">MEI</SelectItem>
                        <SelectItem value="lucro_presumido">Lucro Presumido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-sky-900">ISS Municipal (%)</Label>
                    <Input type="number" step="0.01" value={pj.iss_pct * 100}
                           onChange={(e) => setPj({ ...pj, iss_pct: (parseFloat(e.target.value) || 0) / 100 })}
                           data-testid="pj-iss" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-sky-900">RBT12 (opcional)</Label>
                    <Input type="number" step="0.01" placeholder="Auto: valor × 12"
                           value={pj.rbt12 || ""}
                           onChange={(e) => setPj({ ...pj, rbt12: e.target.value ? parseFloat(e.target.value) : null })}
                           data-testid="pj-rbt12" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-sky-900">Custo Contador</Label>
                    <Input type="number" step="0.01"
                           placeholder={`Auto: ${fmtBRL((tabelas?.salario_minimo_nacional || 1518) * 0.5)}`}
                           value={pj.custo_contador || ""}
                           onChange={(e) => setPj({ ...pj, custo_contador: e.target.value ? parseFloat(e.target.value) : null })}
                           data-testid="pj-contador" />
                  </div>
                </div>
                {(pj.regime === "simples_iii" || pj.regime === "simples_v") && (
                  <div className="flex items-center justify-between rounded-md bg-sky-50 border border-sky-200 px-3 py-2">
                    <Label className="text-[12px] font-medium text-sky-900">Fator R aplicado (folha ≥ 28%)</Label>
                    <Switch checked={pj.fator_r}
                            onCheckedChange={(v) => setPj({ ...pj, fator_r: v })}
                            data-testid="pj-fator-r" />
                  </div>
                )}

                {/* Results PJ */}
                {p && (
                  <div className="space-y-2 mt-3 pt-3 border-t-2 border-dashed border-sky-200">
                    <div className="text-[10.5px] uppercase tracking-wider text-sky-900 font-bold mb-1">{p.anexo}</div>
                    <ResultRow label="Valor mensal NF" value={p.valor_mensal_nf} />
                    {p.tributos?.aliquota_efetiva_pct !== undefined && (
                      <ResultRow label={`Alíquota efetiva (${fmtPct(p.tributos.aliquota_efetiva_pct)})`} value={p.total_tributos} negative />
                    )}
                    {p.tributos?.composicao && (
                      <div className="ml-4 mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] text-sky-800">
                        {Object.entries(p.tributos.composicao).map(([k, v]) => (
                          <div key={k} className="flex justify-between font-mono">
                            <span className="text-sky-600">{k}</span>
                            <span>{fmtBRL(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <ResultRow label="Contador (1/2 SM)" value={p.custo_contador} negative />

                    <div className="rounded-md bg-sky-50 border border-sky-200 p-2 text-[10.5px] text-sky-800 mt-2">
                      <div className="font-semibold mb-0.5">Observações:</div>
                      <div>• Sem férias, 13º, FGTS</div>
                      <div>• Sem INSS/IRRF de pessoa física</div>
                      <div>• Contador padrão: 50% do salário mínimo</div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-sky-100/50 border border-sky-300 p-3" data-testid="pj-result-liquido">
                        <div className="text-[10px] uppercase tracking-wider text-sky-900 font-bold mb-1">Líquido Colaborador</div>
                        <div className="text-xl font-bold text-sky-800 tabular-nums">{fmtBRL(p.liquido_colaborador_mensal)}</div>
                        <div className="text-[10.5px] text-sky-700 mt-0.5">/ mês</div>
                      </div>
                      <div className="rounded-lg bg-sky-100/50 border border-sky-300 p-3" data-testid="pj-result-custo">
                        <div className="text-[10px] uppercase tracking-wider text-sky-900 font-bold mb-1">Custo Empresa</div>
                        <div className="text-xl font-bold text-sky-800 tabular-nums">{fmtBRL(p.custo_empresa_mensal)}</div>
                        <div className="text-[10.5px] text-sky-700 mt-0.5">/ mês · {fmtBRL(p.custo_empresa_anual)} / ano</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== COMPARATIVO FINAL ===== */}
          {result && (
            <div className={`rounded-xl border-2 ${semEmpresa.border} ${semEmpresa.bg} shadow-md`} data-testid="comparativo-final">
              <div className="px-4 py-3 bg-gradient-to-r from-[#234935] via-[#3a7559] to-[#234935] rounded-t-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Sparkles className="h-4 w-4 text-[#C5A059]" />
                  <h3 className="font-bold tracking-wide">RESULTADO COMPARATIVO FINAL</h3>
                </div>
                <span className="text-[10px] uppercase tracking-wide text-emerald-100 bg-white/10 px-2 py-0.5 rounded">
                  Vigência {result.vigencia_tabelas}
                </span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <KpiCard label="Líquido CLT" value={fmtBRL(cm.liquido_colaborador.clt_mensal)}
                          sub="por mês" icon={Wallet} accent="emerald" />
                  <KpiCard label="Líquido PJ" value={fmtBRL(cm.liquido_colaborador.pj_mensal)}
                          sub="por mês" icon={Wallet} accent="sky" />
                  <KpiCard label="Custo Empresa CLT" value={fmtBRL(cm.custo_empresa.clt_anual)}
                          sub="por ano" icon={Building2} accent="emerald" />
                  <KpiCard label="Custo Empresa PJ" value={fmtBRL(cm.custo_empresa.pj_anual)}
                          sub="por ano" icon={Building2} accent="sky" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                  <div className={`rounded-lg p-4 border-2 ${semEmpresa.border} ${semEmpresa.bg}`} data-testid="kpi-economia">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10.5px] uppercase tracking-wider font-bold">Economia Empresa</div>
                      {cm.custo_empresa.economia_anual >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                    </div>
                    <div className={`text-3xl font-bold tabular-nums ${semEmpresa.text}`}>{fmtPct(cm.custo_empresa.economia_pct)}</div>
                    <div className="text-[11.5px] mt-1 font-medium">{fmtBRL(cm.custo_empresa.economia_anual)} / ano</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{fmtBRL(cm.custo_empresa.economia_mensal)} / mês</div>
                  </div>
                  <div className="rounded-lg p-4 border-2 border-[#C5A059]/40 bg-gradient-to-br from-[#FBF8F0] to-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#8A6B2B]">Diferença Colaborador</div>
                      <BadgePercent className="h-5 w-5 text-[#C5A059]" />
                    </div>
                    <div className="text-3xl font-bold tabular-nums text-[#8A6B2B]">{fmtPct(cm.liquido_colaborador.diferenca_pct)}</div>
                    <div className="text-[11.5px] mt-1 font-medium">{fmtBRL(cm.liquido_colaborador.diferenca_anual)} / ano</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{cm.liquido_colaborador.diferenca_anual >= 0 ? "PJ líquido superior" : "CLT líquido superior"}</div>
                  </div>
                  <div className="rounded-lg p-4 border-2 border-slate-200 bg-white">
                    <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-700 mb-2 flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" /> Recomendação
                    </div>
                    <p className="text-[12.5px] text-slate-700 leading-relaxed">{cm.recomendacao}</p>
                  </div>
                </div>

                {/* Comparison Chart */}
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                    Comparativo Anual — CLT × PJ
                  </div>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={compareData} margin={{ top: 10, right: 10, bottom: 5, left: -8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#374151" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false}
                               tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                 formatter={(v) => fmtBRL(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="CLT" fill="#059669" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="PJ" fill="#0284C7" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Save scenario */}
                <div className="mt-4 rounded-lg bg-white border border-border p-3 flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px] uppercase tracking-wider">Nome do cenário</Label>
                    <Input value={nomeCenario} onChange={(e) => setNomeCenario(e.target.value)}
                           placeholder="Ex: Analista Sênior · TI"
                           data-testid="sim-cenario-nome" />
                  </div>
                  <div className="w-[140px]">
                    <Label className="text-[11px] uppercase tracking-wider">Competência</Label>
                    <div className="flex gap-1">
                      <Select value={String(competenciaMes)} onValueChange={(v) => setCompetenciaMes(parseInt(v))}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={String(competenciaAno)} onValueChange={(v) => setCompetenciaAno(parseInt(v))}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026, 2027].map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={saveSimulation}
                          className="h-10 bg-[#234935] hover:bg-[#2c5c43]"
                          data-testid="sim-save">
                    <Save className="h-3.5 w-3.5 mr-1.5" /> Salvar no Histórico
                  </Button>
                </div>
              </div>
            </div>
          )}

          {calculating && !result && <GridSkeleton rows={4} />}
        </TabsContent>

        {/* ===== HISTÓRICO & DASHBOARD ===== */}
        <TabsContent value="historico" className="space-y-4 mt-4">
          <CltPjReportsHistoryPanel companyId={selectedCompany?.id || null} />
          <CltPjScheduledReportsPanel companyId={selectedCompany?.id || null} baseInputs={{ clt, pj, competencia_ano: competenciaAno }} />
          <Section kicker="Filtros" title="Dashboard de Simulações" testId="dash-filters">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-[140px]">
                <Label className="text-[11px] uppercase tracking-wider">Mês</Label>
                <Select value={String(dashFilters.month || "all")}
                        onValueChange={(v) => setDashFilters({ ...dashFilters, month: v === "all" ? null : parseInt(v) })}>
                  <SelectTrigger data-testid="dash-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <Label className="text-[11px] uppercase tracking-wider">Ano</Label>
                <Select value={String(dashFilters.year)}
                        onValueChange={(v) => setDashFilters({ ...dashFilters, year: parseInt(v) })}>
                  <SelectTrigger data-testid="dash-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={loadDashboard} className="h-10 bg-[#234935] hover:bg-[#2c5c43]" data-testid="dash-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Atualizar
              </Button>
            </div>
          </Section>

          {history && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dash-kpis">
                <KpiCard label="Total simulações" value={history.kpis.total_simulacoes} icon={Calculator} accent="slate" />
                <KpiCard label="Economia média" value={fmtPct(history.kpis.economia_media_pct)}
                        icon={BadgePercent}
                        accent={history.kpis.economia_media_pct > 10 ? "emerald" : history.kpis.economia_media_pct > 0 ? "ochre" : "rose"} />
                <KpiCard label="Economia total/ano" value={fmtBRL(history.kpis.economia_total_anual)}
                        icon={DollarSign} accent="emerald" />
                <KpiCard label="PJ vantajoso" value={history.kpis.cenarios_pj_vantajosos} icon={TrendingUp} accent="sky" />
                <KpiCard label="CLT vantajoso" value={history.kpis.cenarios_clt_vantajosos} icon={TrendingDown} accent="emerald" />
              </div>

              {history.evolution.length > 0 && (
                <Section kicker="Evolução" title="Economia média % por competência">
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={history.evolution} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtPct(v)} />
                        <Line type="monotone" dataKey="economia_media_pct" stroke="#234935" strokeWidth={2.5} name="Economia %" dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              )}

              {history.by_regime.length > 0 && (
                <Section kicker="Por regime" title="Economia média por regime tributário">
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={history.by_regime} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
                        <YAxis type="category" dataKey="regime" tick={{ fontSize: 11, fill: "#374151" }}
                               axisLine={false} tickLine={false} width={140} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtPct(v)} />
                        <Bar dataKey="economia_media_pct" radius={[0, 4, 4, 0]}>
                          {history.by_regime.map((r) => (
                            <Cell key={r.regime} fill={
                              r.economia_media_pct > 10 ? "#059669" :
                              r.economia_media_pct > 0 ? "#D97706" : "#E11D48"
                            } />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              )}

              <Section kicker="Histórico" title="Simulações salvas">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-border">
                        <th className="text-left px-3 py-2">Cenário</th>
                        <th className="text-left px-3 py-2">Competência</th>
                        <th className="text-right px-3 py-2">Salário CLT</th>
                        <th className="text-right px-3 py-2">Valor PJ</th>
                        <th className="text-left px-3 py-2">Regime</th>
                        <th className="text-right px-3 py-2">Economia</th>
                        <th className="text-right px-3 py-2">Anual</th>
                        <th className="text-left px-3 py-2">Por</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.items.map((it) => {
                        const s = semColor(it.semaforo);
                        return (
                          <tr key={it.id} className="border-b border-border last:border-0">
                            <td className="px-3 py-2 font-medium">{it.nome_cenario}</td>
                            <td className="px-3 py-2 font-mono text-[11.5px]">{it.competencia}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtBRL(it.salario_clt)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtBRL(it.valor_pj)}</td>
                            <td className="px-3 py-2 text-[11.5px]">{it.regime}</td>
                            <td className={`px-3 py-2 text-right tabular-nums font-semibold ${s.text}`}>{fmtPct(it.economia_pct)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${s.text}`}>{fmtBRL(it.economia_anual)}</td>
                            <td className="px-3 py-2 text-[11px] text-muted-foreground">{it.created_by_name || "—"}</td>
                            <td className="px-2 py-2">
                              <Button variant="ghost" size="sm" onClick={() => deleteSimulation(it.id)}
                                      className="text-rose-600 h-7 text-[11px]">Excluir</Button>
                            </td>
                          </tr>
                        );
                      })}
                      {history.items.length === 0 && (
                        <tr><td colSpan={9} className="text-center py-8 text-muted-foreground italic">Nenhuma simulação no período.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          )}
        </TabsContent>
      </Tabs>

      <CltPjReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        result={result}
        inputs={{ clt, pj }}
        meta={{
          empresa: selectedCompany?.nome_fantasia || selectedCompany?.razao_social || "",
          cargo: prefilledCargo || "",
          responsavel: user?.name || user?.email || "",
          nomeCenario,
          competenciaAno,
          logoUrl: selectedCompany?.logo_url || "",
          companyId: selectedCompany?.id || null,
        }}
      />

      <MultiScenarioDialog
        open={multiOpen}
        onClose={() => setMultiOpen(false)}
        baseInputs={{ clt, pj }}
        meta={{
          empresa: selectedCompany?.nome_fantasia || selectedCompany?.razao_social || "",
          responsavel: user?.name || user?.email || "",
          competenciaAno,
          logoUrl: selectedCompany?.logo_url || "",
          companyId: selectedCompany?.id || null,
        }}
      />
    </div>
  );
}

function ResultRow({ label, value, negative, bold }) {
  return (
    <div className={`flex items-center justify-between text-[12px] py-0.5 ${bold ? "font-bold pt-1 border-t border-dashed border-emerald-300/50" : ""}`}>
      <span className="text-slate-700">{label}</span>
      <span className={`font-mono tabular-nums ${negative ? "text-rose-700" : "text-slate-900"}`}>
        {negative && "− "}{fmtBRL(value)}
      </span>
    </div>
  );
}
